// ** Third Party Components
import { useQuery } from "@tanstack/react-query"

// ** API & Context
import { get } from "../../api/contact"
import { userContext } from "../../context/user"

// ** ¿Tiene la verificación en dos pasos activa?
//
// Es la condición para poder usar BlockPay: sin ella no se entra a ninguna pantalla salvo el
// Perfil, que es donde se activa (ver layouts/VerticalLayout.js).
//
// Vive aparte de `useTwoFactorAuth` —que es el que la activa, la desactiva y valida códigos—
// porque esto es una sola pregunta que hacen dos sitios a la vez: el layout, para decidir si
// deja pasar, y el menú, para decidir si se pinta. Con el useEffect de aquel hook cada uno
// lanzaba su propia petición al mismo endpoint; con react-query la respuesta se comparte.
//
// El dato vive en la ficha del usuario, no en la sesión: la sesión guarda un `enabledOtp` del
// momento del login, y si alguien activa el 2FA ahora mismo esa copia ya no dice la verdad.
//
// ── Sesiones de empleado ──────────────────────────────────────────────────────────────────
//
// Un empleado puede entrar de dos maneras, y el modal del código IBO se las ofrece con esas
// palabras: «Ingresar a mi cuenta» —su propia sesión— o «Ingresar como ibo», que asume la
// sesión de otra persona para atenderla. `userEmployee` está puesto en las dos, así que no
// sirve para distinguirlas; lo que las separa es de quién es la sesión.
//
// En su propia sesión no hay nada especial: el 2FA se activa y se comprueba igual que en
// cualquier otro usuario, sobre su ficha. Tiene que ser la ficha y no la copia del login,
// porque esa copia se escribe al entrar y no se vuelve a tocar: usarla dejaría al empleado
// bloqueado para siempre —activaría el 2FA, la página recargaría, y el dato viejo seguiría
// diciendo que no lo tiene—.
//
// Asumiendo la sesión de un IBO sí cambia: la ficha que se consultaría es la del IBO, y su
// 2FA no dice nada de quién está tecleando. Ahí manda el del empleado, que viaja en la sesión
// y ya se validó al entrar: si lo tiene activo, `signIn` lo mandó a meter el código antes de
// dejarlo pasar. Exigir en cambio el del IBO dejaría al empleado bloqueado por una cuenta que
// no es suya, y la única salida sería activárselo desde fuera: decidir por otra persona sobre
// su seguridad y atar su segundo factor a un dispositivo ajeno.
const useTwoFactorStatus = () => {
  const { user, userEmployee } = userContext()
  const userId = user?.userId

  // La sesión es de otra persona: un empleado atendiendo a un IBO.
  const isImpersonating = Boolean(userEmployee) && userId !== userEmployee?.userId

  const { data, isLoading } = useQuery({
    queryKey: ["twoFactorStatus", userId],
    // La ficha del IBO no decide nada aquí, así que no se pide: sería una consulta cuya
    // respuesta se descarta.
    enabled: Boolean(userId) && !isImpersonating,
    queryFn: async () => await get(userId)
  })

  return {
    enabled: isImpersonating ? Boolean(userEmployee?.enabledOtp) : Boolean(data?.enabledOtp),
    // Mientras no se sabe no se decide nada: dar por hecho que no está activo mandaría al
    // Perfil a quien sí lo tiene, cada vez que entra. Atendiendo a un IBO se sabe al instante,
    // porque el dato viaja en la sesión y no hay nada que esperar.
    isLoading: isLoading && Boolean(userId) && !isImpersonating
  }
}

export default useTwoFactorStatus
